import { checkPermission } from 'components/PermissionHide/checkPermission';
import { clearUser, setUser } from 'errors/errorHandler';
import { setHotjarUser } from 'hotjar';
import { setIntercomUser } from 'intercom';
import { toast } from 'react-toastify';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, tap, finalize } from 'rxjs/operators';
import { ACCOUNTS_ENV, ACCOUNTS_TOKEN } from 'settings';
import store from 'store';
import Cookies from 'universal-cookie';
import { BlackBeltOrMore } from 'v2/interfaces/elite';

import type { TopbarProps } from '@eduzz/ui-layout/Topbar';
import type { IAccess, IAccountInfo, IAuthAccounts, ISwitchAccount } from 'v2/interfaces/account';
import type { IApiResponse } from 'v2/interfaces/api';
import type { FeatureAccessData } from 'v2/interfaces/featureAccess';
import type { IHasUserBetaModule } from 'v2/interfaces/shopping';
import type { User } from 'v2/interfaces/user';

import apiService from './api';
import chatBotService from './chatBot';
import tokenService from './token';

import type { ApiService } from './api';
import type { TokenService } from './token';
import type { HotjarUser } from 'hotjar';
import type { IAccessPolicyPermissionsAction } from 'store/Orbita/AccessPolicy/interfaces';

const VERTICAL_COOKIE = 'vertical-switchaccount';

export class EduzzAccountsService {
  private accesses$ = new Rx.BehaviorSubject<IAccess[]>([]);
  private isLogged$ = new Rx.BehaviorSubject<boolean>(false);
  private permissions$ = new Rx.BehaviorSubject<IAccessPolicyPermissionsAction>(null);
  private newRefundPermission$ = new Rx.BehaviorSubject<boolean>(false);
  private user$ = new Rx.BehaviorSubject<User>(null);
  private originalUser$ = new Rx.BehaviorSubject<User>(null);
  private hasInit$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingAccount$ = new Rx.BehaviorSubject<boolean>(true);
  private hasSession$ = new Rx.BehaviorSubject<boolean>(false);
  private betaModules$ = new Rx.BehaviorSubject<string[]>([]);
  private loadingBetaModules$ = new Rx.BehaviorSubject<boolean>(true);
  private tours$ = new Rx.BehaviorSubject<string[]>([]);
  private loadingTours$ = new Rx.BehaviorSubject<boolean>(true);
  private featureAccessData$ = new Rx.BehaviorSubject<FeatureAccessData>(null);
  private loadingfeatureAccessData$ = new Rx.BehaviorSubject<boolean>(true);
  private cookies = new Cookies();

  constructor(private tokenService: TokenService, private apiService: ApiService) {}

  public login(registerScreen?: boolean) {
    return new Promise((resolve) => {
      return (window as any).Eduzz.Accounts.login(ACCOUNTS_TOKEN, {
        logo: 'https://orbita.eduzz.com/img/logo-myeduzz-login.png',
        env: ACCOUNTS_ENV,
        register: registerScreen ?? false,
        isPartnerCreate: true,
        isPartnerLogin: true,
      }).subscribe(resolve);
    });
  }

  public initialize(registerScreen?: boolean) {
    this.hasInit$.next(true);

    Rx.from(this.login(registerScreen))
      .pipe(
        switchMap((token) => this.apiService.post('/auth/accounts', { token })),
        catchError((err) => {
          toast.error(err);

          setTimeout(() => {
            this.hasInit$.next(false);
            this.logout();
          }, 5000);

          return Rx.of({});
        })
      )
      .subscribe((response: IApiResponse<IAuthAccounts>) => {
        this.clearLocalStorage();
        if (response.data) {
          const { ssid, token, refreshToken, customer } = response.data;

          this.tokenService.setToken(token);
          this.tokenService.setRefreshToken(refreshToken);
          this.loadAccesses(ssid);
          this.tokenService.setAccountsSsid(ssid);

          if (customer?.account_id) this.tokenService.setAccountsId(customer.account_id);
          if (customer?.qualification_key) this.tokenService.setQualificationKey(customer.qualification_key);
        }
      });
  }

  public updateUrl() {
    const currentUrl = `${window.location.origin}${window.location.pathname}`;
    const oldUrl = window.location.href;
    const newUrl = new URL(oldUrl);
    const params = new URLSearchParams(newUrl.search.slice(1));
    params.delete('accounts_token');
    params.delete('accounts_login_token');
    const newParams = params.toString() ? `?${params.toString()}` : '';
    window.history.pushState(null, null, `${currentUrl}${newParams}`);
  }

  public changeAccount(email: string) {
    this.loadingAccount$.next(true);

    this.user$.next(null);
    this.originalUser$.next(null);
    this.permissions$.next(null);
    this.isLogged$.next(false);
    this.apiService.post('/access-control/switchaccount', { email }).subscribe(({ data }: IApiResponse<ISwitchAccount>) => {
      this.setVerticalCookie(email);
      this.tokenService.setToken(data.token);
      this.isLogged$.next(true);
      this.loadAccountDetails();
      this.loadPermissions();
      this.loadBetaModules();
      this.loadfeatureAccessData();
      this.loadTours();
      this.loadingAccount$.next(false);
    });
  }

  public loadAccesses(ssid: string): void {
    this.apiService.get('/access-control/accesses').subscribe((response: IApiResponse<IAccess[]>) => {
      const { data: accesses } = response;
      const onlyOneAccess = accesses?.length === 1;

      if (onlyOneAccess) {
        this.loadAccountDetails();
        this.loadPermissions();
        this.loadBetaModules();
        this.loadTours();
        this.loadfeatureAccessData();
        this.isLogged$.next(true);
      }

      this.setAccesses(accesses);
      this.loadingAccount$.next(false);

      const lastEmailSelected = this.getVerticalCookie();
      const multipleAccesses = accesses?.length > 1;

      const isSameAccount = accesses.some((a) => a.email === lastEmailSelected);

      if (lastEmailSelected && multipleAccesses && isSameAccount) this.changeAccount(lastEmailSelected);

      localStorage.setItem('ssid', ssid);

      this.updateUrl();
    });
  }

  public loadBetaModules(): void {
    this.loadingBetaModules$.next(true);
    this.apiService
      .get('/auth/beta-modules')
      .pipe(
        map((response) => [...(response.data ?? []), 'toolbar']),
        tap((data) => {
          this.betaModules$.next(data);
        }),
        finalize(() => this.loadingBetaModules$.next(false))
      )
      .subscribe();
  }

  public getBetaModules(): Rx.Observable<string[]> {
    return this.betaModules$;
  }

  public getTours(): Rx.Observable<string[]> {
    return this.tours$;
  }

  public verifyUserHasBetaModule(module: string): boolean {
    return this.betaModules$.value.includes(module);
  }

  public verifyUserHasBetaModuleByProducerId(producerId: number, module: string): Rx.Observable<IHasUserBetaModule> {
    return this.apiService
      .get('auth/is-beta-customer-in-module', {
        beta_customer_id: producerId,
        module,
      })
      .pipe(tap((response) => response.data.data));
  }

  public getAccesses(): Rx.Observable<IAccess[]> {
    return this.accesses$;
  }

  public setAccesses(accesses: IAccess[]): void {
    this.accesses$.next(accesses);
  }

  public getIsLogged(): Rx.Observable<boolean> {
    return this.isLogged$;
  }

  public getLoadingAccount(): Rx.Observable<boolean> {
    return this.loadingAccount$;
  }

  public getLoadingBetaModules(): Rx.Observable<boolean> {
    return this.loadingBetaModules$;
  }

  public getUser(): Rx.BehaviorSubject<User> {
    return this.user$;
  }

  public getUserToolbar(): Rx.Observable<TopbarProps['user'] & { isPartner: boolean; liveHelperToken?: string }> {
    return this.getUser().pipe(
      map((user) => {
        if (!user) return null;

        return {
          id: user.id,
          name: user.business_name ?? user.full_name,
          email: user.email,
          avatar: user.photo,
          belt: this.getBeltById(user.current_elite_level?.id),
          tag: !user.acceptence_terms ? null : user.is_clube_black ? 'unity' : 'pro',
          isSupport: user.is_support,
          isPartner: user.acceptence_terms,
          supportId: user.support_id,
          isClubeBlack: user.is_clube_black,
          liveHelperToken: user.live_helper_token,
        };
      })
    );
  }

  public getNewRefundPermission(): Rx.BehaviorSubject<boolean> {
    return this.newRefundPermission$;
  }

  public setNewRefundPermission(user: any): void {
    this.apiService
      .get(`/access-control/${user.id}/permission-new-refund-service?is_producer=${user.producer}`)
      .pipe(map(({ data }) => data))
      .subscribe((data) => {
        this.newRefundPermission$.next(data.can_access_new_refund);
      });
  }

  public getUserVertical() {
    return Rx.combineLatest([
      this.getUser(),
      this.getOriginalUser(),
      this.getPermissions(),
      this.getBetaModules(),
      this.getNewRefundPermission(),
    ]).pipe(
      map(([user, originalUser, permissions, betas, newRefundPermission]) => {
        function getUserTag(user: User) {
          if (user.is_clube_black) return 'unity';
          if (user.acceptence_terms) return 'pro';
          return undefined;
        }

        const isAccessPolicy = user.id !== originalUser.id;

        return {
          id: user.id,
          email: user.email,
          document: user.document,
          name: user.name,
          avatar: user.photo,
          tag: getUserTag(user),
          isClubeBlack: user.is_clube_black,
          betas,
          isAccessPolicy,
          originalUserId: isAccessPolicy ? originalUser.id : null,
          belt: user.current_elite_level.name,
          isSupport: user.is_support,
          supportId: user.is_support ? user.support_id : null,
          is: {
            producer: user.producer,
            affiliate: user.affiliate,
            coproducer: user.coproducer,
            customer: user.beta_customer,
            partner: user.general_term,
            agency: user.is_agency,
          },
          permissions: {
            owned: permissions.owned,
            actions: permissions.actions,
            isAbleToUseRefundService: newRefundPermission,
          },
          terms: {
            general: user.general_term,
            acceptence: user.acceptence_terms,
          },
        } as Omit<MyEduzzCurrentUser, 'checkPermission'>;
      })
    );
  }

  public getOriginalUser(): Rx.Observable<User> {
    return this.originalUser$;
  }

  public setUser(user: IAccountInfo): void {
    const { customer, original_customer } = user;
    const { id, email, full_name, current_elite_level } = customer;

    setUser(user.customer);
    chatBotService.register(customer.id);
    const belt = this.getBeltById(current_elite_level.id);
    setHotjarUser({
      id,
      email,
      name: full_name,
      belt,
      isAccessControl: customer.id === original_customer.id,
    } as HotjarUser);
    setIntercomUser(user, belt);
    store.dispatch({ type: '@@USER/SET_USER', user });
    store.dispatch({ type: '@@ORIGINAL_USER/SET_USER', user });
    this.user$.next(customer);
    this.setNewRefundPermission(customer);
    this.originalUser$.next(original_customer);
  }

  public loadAccountDetails(): void {
    this.apiService.get('/auth/details').subscribe(({ data }) => this.setUser(data));
  }

  public loadTours(): void {
    this.loadingTours$.next(true);
    this.apiService.get('/profile/tours').subscribe(({ data }) => {
      this.tours$.next(data);
      this.loadingTours$.next(false);
    });
  }

  public loadPermissions(): void {
    this.apiService.get('/access-control/permissions').subscribe(({ data }) => this.setPermissions(data));
  }

  public getPermissions(): Rx.Observable<IAccessPolicyPermissionsAction> {
    return this.permissions$;
  }

  public setPermissions(permissions: any): void {
    const permissionMap = {
      owned: permissions.owned,
      actions: Object.keys(permissions.accesses).reduce((acc, key) => {
        acc.push(...permissions.accesses[key].actions.map((a: { fullname: any }) => a.fullname));
        return acc;
      }, []),
    } as IAccessPolicyPermissionsAction;

    //TODO ADD NO REDUX PQ USA EM VARIAS TELAS AINDA, RETIRAR APÓS MIGRAR AS TELAS PARA V2 (RX) x(
    store.dispatch({
      type: '@@service/ACCESS_POLICY/PERMISSIONS_SUCCESS',
      payload: {
        data: {
          data: permissionMap,
        },
      },
    });

    //TODO ADD NO REDUX PQ USA EM VARIAS TELAS AINDA, RETIRAR APÓS MIGRAR AS TELAS PARA V2 (RX) x(
    this.permissions$.next(permissionMap);
  }

  public logout() {
    this.setVerticalCookie(null);
    this.clearLocalStorage();
    clearUser();
    this.loadingAccount$.next(true);
    this.user$.next(null);
    this.originalUser$.next(null);
    this.permissions$.next(null);
    this.isLogged$.next(false);
    this.tokenService.clearToken();
    window.history.pushState(null, null, '/');
    (window as any).Eduzz.Accounts.logout({ env: ACCOUNTS_ENV });
    // window.history.pushState(null, null, null);
  }

  public hasPermission(action: string): boolean {
    return checkPermission(this.permissions$.value, action);
  }

  public changePassword() {
    return apiService.get('/auth/changepass');
  }

  public isInit(): Rx.Observable<boolean> {
    return this.hasInit$;
  }

  public hasSession(): Rx.Observable<boolean> {
    return this.hasSession$;
  }

  public saveTourAction(tour: string) {
    this.tours$.next([...this.tours$.value, tour]);
    return this.apiService.post('/profile/tours', { tour });
  }

  public getLoadingTours() {
    return this.loadingTours$;
  }

  public isBlackBeltOrMore() {
    const userEliteLevelId = this.user$.value?.current_elite_level?.id;
    const idsQualified = Object.values(BlackBeltOrMore);
    return idsQualified.includes(userEliteLevelId);
  }

  public getBeltById(id: number): string {
    const belts = {
      100: 'Red Belt',
      101: 'Orange Belt',
      102: 'Green Belt',
      103: 'Black Belt',
      104: 'Golden Belt',
      109: 'Black Belt D2',
      110: 'Black Belt D3',
      111: 'Black Belt D4',
      112: 'Black Belt D5',
      113: 'Black Belt D6',
      114: 'Black Belt D7',
      115: 'Black Belt D8',
      116: 'Black Belt D9',
      300: 'Sensei',
      302: 'Golden Belt D20',
      303: 'Golden Belt D30',
      304: 'Golden Belt D40',
      305: 'Golden Belt D50',
      306: 'Golden Belt D60',
      307: 'Golden Belt D70',
      308: 'Golden Belt D80',
      309: 'Golden Belt D90',
    };

    return belts[id] || null;
  }

  public userHasBelt(beltId: number[]) {
    return beltId.includes(this.user$.value?.current_elite_level?.id);
  }

  public checkQualificationRedirect(accountId: string) {
    return this.apiService
      .get(`/qualification/check-redirect/${accountId}`)
      .toPromise()
      .then((res) => res.data);
  }

  public checkPendingQualification(eduzzId: number) {
    return this.apiService
      .get(`/qualification/check-pending/${eduzzId}`)
      .toPromise()
      .then((res) => res.data);
  }

  public updateUserDocument(document: string): void {
    const customer = { ...this.user$.value };
    customer.document = document;
    this.setUser({ customer, original_customer: this.originalUser$.value });
  }

  public loadfeatureAccessData(): void {
    this.loadingfeatureAccessData$.next(true);
    this.apiService
      .get('/customers/feature-access')
      .pipe(
        map((response) => response.data ?? []),
        tap((data) => {
          this.featureAccessData$.next(data);
        }),
        finalize(() => this.loadingfeatureAccessData$.next(false))
      )
      .subscribe();
  }

  public getfeatureAccessData(): Rx.BehaviorSubject<FeatureAccessData> {
    return this.featureAccessData$;
  }

  private clearLocalStorage(): void {
    localStorage.removeItem('ssid');
    localStorage.removeItem('id');
  }

  private getVerticalCookie(): string {
    return this.cookies.get(VERTICAL_COOKIE) as string;
  }

  private setVerticalCookie(email: string | null) {
    const host = window.location.hostname.split('.');
    host.shift();

    if (!email) {
      this.cookies.remove(VERTICAL_COOKIE, { path: '/', domain: `.${host.join('.')}` });
      return;
    }

    this.cookies.set(VERTICAL_COOKIE, email, { path: '/', domain: `.${host.join('.')}` });
  }
}

const eduzzAccountsService = new EduzzAccountsService(tokenService, apiService);
export default eduzzAccountsService;
